'use client';
import React, { useState } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    type: 'general',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const contactInfo = [
    {
      icon: (
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'العنوان',
      value: 'عدن، الجمهورية اليمنية',
    },
    {
      icon: (
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      title: 'هاتف',
      value: '+967 2 234 567',
    },
    {
      icon: (
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: 'البريد الإلكتروني',
      value: 'info@mofa.gov.ye',
    },
    {
      icon: (
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'ساعات العمل',
      value: 'الأحد — الخميس: 8:00 ص — 4:00 م',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col font-tajawal" dir="rtl">
      <Navbar activePage="/contact" />
      <main className="flex-1 bg-background">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-accent py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 text-white border border-white/20 text-xs font-medium mb-4">
              📧 تواصل معنا
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">تواصل معنا</h1>
            <p className="text-white/70 max-w-xl mx-auto text-sm">
              فريقنا متاح لمساعدتك على مدار الساعة
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-4">
              {contactInfo.map((info) => (
                <div key={info.title} className="bg-white rounded-xl border border-border p-5 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    {info.icon}
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-foreground">{info.title}</div>
                    <div className="text-sm text-muted-foreground mt-0.5" dir="ltr" style={{ textAlign: 'right' }}>{info.value}</div>
                  </div>
                </div>
              ))}

              {/* Emergency */}
              <div className="bg-red-50 rounded-xl border border-red-200 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-red-500 text-lg">🚨</span>
                  <span className="font-bold text-red-800 text-sm">خدمة الطوارئ</span>
                </div>
                <p className="text-xs text-red-700 leading-relaxed">
                  للحالات الطارئة لليمنيين في الخارج، تواصل معنا عبر الخط الساخن أو البريد الإلكتروني على مدار الساعة.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="bg-white rounded-2xl border border-border p-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">تم إرسال رسالتك</h3>
                  <p className="text-muted-foreground text-sm">سيتواصل معك فريقنا خلال 24 ساعة</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-4 text-sm text-accent hover:underline"
                  >
                    إرسال رسالة أخرى
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-border p-8">
                  <h2 className="text-xl font-bold text-foreground mb-6">أرسل رسالة</h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">الاسم الكامل *</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="input-gov"
                          placeholder="محمد علي"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">البريد الإلكتروني *</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="input-gov"
                          placeholder="example@email.com"
                          dir="ltr"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">رقم الهاتف</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="input-gov"
                          placeholder="+967 XXX XXX XXX"
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">نوع الاستفسار</label>
                        <select
                          value={formData.type}
                          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                          className="input-gov"
                        >
                          <option value="general">استفسار عام</option>
                          <option value="visa">تأشيرة</option>
                          <option value="passport">جواز سفر</option>
                          <option value="consular">خدمة قنصلية</option>
                          <option value="technical">دعم فني</option>
                          <option value="emergency">حالة طارئة</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">الموضوع *</label>
                      <input
                        type="text"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="input-gov"
                        placeholder="موضوع رسالتك"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">الرسالة *</label>
                      <textarea
                        required
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="input-gov resize-none"
                        placeholder="اكتب رسالتك هنا..."
                      />
                    </div>
                    <button type="submit" className="gov-btn-primary w-full justify-center">
                      إرسال الرسالة
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
